import { Injectable } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ValidatorPatterns } from '@shared/validatorpatterns';
import { IDataType, IItemOperationSetup, ISortSetting, ITerminalTemplate, ITerminalTemplateData } from '../terminaltemplateAdministration.service';
import { IValidator, objectValidator, ObjectValidators, ValidatorMap } from './objectValidator';

const translationPrefix = 'mes.administration.terminaltemplate';

/** The dialogs cap `detailView` at 200 000 characters (`create-terminaltemplate.component.ts:62`, `edit-terminaltemplate.component.ts:57`, and `maxlength="200000"` on both textareas). Exported so the import cannot become a back door for a payload the UI would reject. */
export const TERMINAL_TEMPLATE_DETAIL_VIEW_MAX_LENGTH = 200000;

/** Mirrors the per-item `maxLength` in the dialog's `createListItem` (`:133`-`:141`). */
export const ITEM_OPERATION_SETUP_MAX_LENGTH = 255;

/**
 * Requires `groupList` / `unitList` to be a JSON array, with this entity's message.
 *
 * The check itself is `ObjectValidators.isArray` — shared, because nothing about "must be an array" is terminal-template-specific. Only the message is: it names _item setup objects_, which is more use to someone editing the cell than the generic wording.
 *
 * Worth keeping in mind why the check exists at all: `ObjectValidators.array` returns no errors for a non-array, so without this a cell holding `{}` validates clean and reaches the API — the hole the pre-migration import's blind `JSON.parse(row.groupList) as IItemOperationSetup[]` left open .
 */
const mustBeItemList: IValidator<IItemOperationSetup[]> = ObjectValidators.isArray(`${translationPrefix}.import.errors.not-a-list`);

/**
 * `dataType` / `sort` must be a member of their enum, with this entity's message.
 *
 * `ObjectValidators.oneOf` does the work; the message names the offending value and the valid set, which the generic copy also does — this override exists only so the existing translated string is reused rather than duplicated under a second key.
 */
function itemEnum<T extends string>(allowed: readonly T[]): IValidator<T> {
	return ObjectValidators.oneOf(allowed, `${translationPrefix}.import.errors.invalid-enum-value`);
}

/**
 * Validation for terminal templates. Called only by `TerminalTemplateTransferService`'s import pipeline — the create/edit dialogs do their own validation with Angular reactive-forms `Validators`. The rules below deliberately _mirror_ those dialogs so the importer is neither stricter nor laxer than the UI, but nothing
 * in the dialogs calls this service.
 *
 * **Where mandatory-ness lives.** `TerminalTemplateSchema` marks every column but `alias` as `required: false`, so a blank cell reaches this service instead of being rejected by the parser with a generic `missing-properties` row error. That routes every missing value through `objectValidator`, giving the user the same
 * per-field translated message the dialog gives.
 *
 * **`name` carries no `required`.** The create/edit dialogs validate it with only `maxLength(255)` and `pattern(ValidatorPatterns.Description)` (`create-terminaltemplate.component.ts:53`, `edit-terminaltemplate.component.ts:45`), and the type declares `name?: string` (`terminaltemplateAdministration.service.ts:126`).
 * A nameless template is therefore legitimate and creatable through the UI, so it must re-import from its own export — which the pre-migration import refused. This is a per-entity asymmetry: a _terminal's_ name is mandatory, because its dialog does carry `Validators.required`.
 *
 * **`groupList` / `unitList` are proven, never cast.** They arrive as JSON text in a single Excel cell, so what lands here is whatever `JSON.parse` produced. Each entry is checked against `IItemOperationSetup` through `ObjectValidators.array` + `.object`, whose nested `arrayError` / `objectError` output
 * `ImportErrorPopupComponent.formatValidationErrors` already renders as `groupList[0].source: …` (`import-error-popup.component.ts:79`-`:103`).
 */
@Injectable({ providedIn: 'root' })
export class TerminalTemplateValidationService {
	constructor(private translator: TranslateService) {}

	/**
	 * Per-item rules for `groupList` / `unitList`, mirroring the dialog's `createListItem`: `source` and `title` required + `maxLength(255)`, `dataType` and `sort` required and a member of their enum, `format` unvalidated.
	 *
	 * `source` / `title` need only `required`: it now rejects `''` the way Angular's does, so the explicit `minLength(1)` these once carried — added because the old `required` passed `''`, and JSON can carry an explicit `""` where a blank Excel cell is simply absent — is redundant.
	 *
	 * Note the deliberate asymmetry with the dialog's form control, which is `datatype` (lowercase `t`) bound to the interface's `dataType` (`create-terminaltemplate.component.ts:137`). The import contract accepts the interface spelling only, so an item using `datatype` fails `dataType`'s `required` check with an
	 * error naming the expected key. Deliberately not normalised: a hand-edited file must not be able to drop the field silently.
	 */
	itemOperationSetupValidators(): ValidatorMap<IItemOperationSetup> {
		return {
			source: [ObjectValidators.required, ObjectValidators.maxLength(ITEM_OPERATION_SETUP_MAX_LENGTH)],
			title: [ObjectValidators.required, ObjectValidators.maxLength(ITEM_OPERATION_SETUP_MAX_LENGTH)],
			dataType: [ObjectValidators.required, itemEnum(Object.values(IDataType))],
			// Unvalidated in the dialog — free text describing how to render the value.
			format: [],
			sort: [ObjectValidators.required, itemEnum(Object.values(ISortSetting))],
		};
	}

	validators(validateId: boolean = true): ValidatorMap<ITerminalTemplate> {
		// `required` rejects a null entry, which `.object` alone lets through; `.object` then proves
		// the entry really is an IItemOperationSetup rather than whatever JSON.parse produced.
		const itemList: IValidator<IItemOperationSetup[]> = ObjectValidators.array([ObjectValidators.required, ObjectValidators.object(this.itemOperationSetupValidators())]);

		const data: ValidatorMap<ITerminalTemplateData> = {
			// Both may legitimately be empty or absent. The dialog drops untitled group rows, so a
			// template with no groups is normal (`create-terminaltemplate.component.ts:172`), and it
			// builds `unitList` as an empty FormArray with no non-empty validator while `onSave`
			// gates only on `form.valid` (`:65`, `:169`) — so an empty unitList is UI-creatable too.
			// The pre-migration import rejected an empty `unitList`, and the spec originally said to
			// preserve that rule. It was dropped for the same reason the nameless-template rule
			// was: a template the dialog can create must survive its own
			// export→re-import, or the round-trip guarantee is false. Enforcing a rule the UI does
			// not enforce makes the importer stricter than the app.
			groupList: [mustBeItemList, itemList],
			unitList: [mustBeItemList, itemList],
			detailView: [ObjectValidators.maxLength(TERMINAL_TEMPLATE_DETAIL_VIEW_MAX_LENGTH)],
		};

		const validator: ValidatorMap<ITerminalTemplate> = {
			id: [ObjectValidators.required],
			alias: [ObjectValidators.required, ObjectValidators.maxLength(255), ObjectValidators.pattern(ValidatorPatterns.Alias, `${translationPrefix}.dialog.alias.error.pattern`)],
			// No `required`, which is exactly how the dialog declares it — and `pattern` now skips
			// empty values like Angular's does, so `''` and an absent name both pass without a
			// per-field guard. That is the point: a nameless template the UI can create must
			// survive its own re-import.
			name: [ObjectValidators.maxLength(255), ObjectValidators.pattern(ValidatorPatterns.Description, `${translationPrefix}.dialog.name.error.pattern`)],
			data: [ObjectValidators.required, ObjectValidators.object(data)],
		};

		if (!validateId) {
			delete validator.id;
		}

		return validator;
	}

	validateTerminalTemplate(template: ITerminalTemplate, validateId: boolean = true): Record<string, ValidationErrors> {
		return objectValidator.validateObject(template, this.validators(validateId), this.translator);
	}
}
